// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useState } from 'react';

type GameScoringRuleResponse = {
  gameDefinitionId?: string;
  GameDefinitionId?: string;
  position?: number;
  Position?: number;
  points?: number;
  Points?: number;
};

export type ScoringPointsByGameId = Record<string, number[]>;

export function getMeritLadder(
  scoringPointsByGameId: ScoringPointsByGameId,
  gameId: string,
  weightPercent: number,
): number[] {
  const basePoints = scoringPointsByGameId[gameId];
  if (!basePoints || basePoints.length === 0) {
    return [];
  }
  const multiplier = weightPercent <= 0 ? 0 : weightPercent / 100;
  return basePoints.map((points) => roundToNearestHalf(points * multiplier));
}

export function formatMerits(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

export function formatMeritLadder(ladder: number[], separator = ' / '): string {
  return ladder.map(formatMerits).join(separator);
}

// Mirrors ScoringRuleFormulaService.RoundToNearestHalf (midpoint away from zero).
function roundToNearestHalf(value: number): number {
  return Math.round(value * 2) / 2;
}

export function useGameScoringRules(
  apiBaseUrl: string,
  token: string | null | undefined,
  playerCount: number,
): ScoringPointsByGameId {
  const [scoringPointsByGameId, setScoringPointsByGameId] = useState<ScoringPointsByGameId>({});

  useEffect(() => {
    if (!token || playerCount <= 0) {
      setScoringPointsByGameId({});
      return;
    }

    let cancelled = false;
    const fetchRules = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/game-scoring-rules?playerCount=${playerCount}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          if (!cancelled) setScoringPointsByGameId({});
          return;
        }
        const payload = (await response.json()) as GameScoringRuleResponse[];
        if (cancelled) return;
        const entriesByGameId: Record<string, Array<{ position: number; points: number }>> = {};
        (payload ?? []).forEach((rule) => {
          const gameDefinitionId = rule.gameDefinitionId ?? rule.GameDefinitionId;
          const position = rule.position ?? rule.Position;
          const points = rule.points ?? rule.Points;
          if (!gameDefinitionId || typeof position !== 'number' || typeof points !== 'number' || Number.isNaN(points)) {
            return;
          }
          (entriesByGameId[gameDefinitionId] ??= []).push({ position, points });
        });
        const next: ScoringPointsByGameId = {};
        Object.entries(entriesByGameId).forEach(([gameId, entries]) => {
          next[gameId] = entries.sort((a, b) => a.position - b.position).map((entry) => entry.points);
        });
        setScoringPointsByGameId(next);
      } catch {
        if (!cancelled) setScoringPointsByGameId({});
      }
    };

    fetchRules();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, playerCount, token]);

  return scoringPointsByGameId;
}
